import * as signalR from '@microsoft/signalr';

import {    
    sessionService,   
    ApiEndpoints, 
    ApiKeyManager,
    EnumUtility,
    ErrorModel
} from '../components/ComponentsLibrary';

//import { apiMainFrameBaseUrl } from './config'; // Your API base URL

export class SignalRScrapeService {
  constructor(props) {
    this.connection = null;
    this.pendingRequests = new Map(); // Track pending requests by ID
    this.timeoutDuration = 7730000; // 30 seconds
    this.apiMainFrameBaseUrl = props.apiMainframeUrl;
    //this.apiMainFrameBaseUrl = "https://localhost:7089/api/";
  }

  // Initialize SignalR connection
  async startConnection() {
    if (this.connection) return this.connection;

    const userInfo = sessionService.getUser();
    const legacyUserID = userInfo?.legacyUserID || -123456;

    const connectionApiUrl =  await ApiEndpoints.SignalRScrapeHubConnectionApiUrl();

    try {
      this.connection = new signalR.HubConnectionBuilder()
        .withUrl(`${this.apiMainFrameBaseUrl}${connectionApiUrl.ApiUrl}${legacyUserID}`, {
          headers: {
            'Content-Type': 'application/json',
            'MainframeInteractionApiKey': ApiKeyManager.GetMainframeScrapeApiKey(),
          }
        })
        .withAutomaticReconnect()
        .build();
    } catch (error) {
      console.error("Error creating SignalR connection:", error);
      throw new Error(`Failed to create SignalR connection: ${error.message}`);
    }

    // Setup response handler
    this.connection.on("ResponseReady", this.handleResponseReady.bind(this));

    try {
      await this.connection.start();
      console.log("SignalR connection established");
      return this.connection;
    } catch (err) {
      console.error("SignalR connection failed:", err);
      this.connection = null;
      throw err;
    }
  }

  // Handle incoming response notifications
  async handleResponseReady(incomingId) {
    console.log("Received ResponseReady for ID:", incomingId);
    
    if (!this.pendingRequests.has(incomingId)) {
      console.warn(`Received response for unknown request ID: ${incomingId}`);
      return;
    }

    const { resolve, reject } = this.pendingRequests.get(incomingId);

    const responseApiUrl = await ApiEndpoints.SignalRScrapeResponseApiUrl();
    const completeApiUrl = await ApiEndpoints.SignalRScrapeCompleteApiUrl();

    try {
      // Fetch the actual response
      const res = await fetch(`${this.apiMainFrameBaseUrl}${responseApiUrl.ApiUrl}${incomingId}`, {
        headers: {
          'Content-Type': 'application/json',
          'MainframeInteractionApiKey': ApiKeyManager.GetMainframeScrapeApiKey(), // <--- Add this line
        }
      });
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      
      const rawJson = await res.text();
      const responseData = JSON.parse(rawJson);
      
      // Mark request as complete on server
      await fetch(`${this.apiMainFrameBaseUrl}${completeApiUrl.ApiUrl}${incomingId}`, { 
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'MainframeInteractionApiKey': ApiKeyManager.GetMainframeScrapeApiKey(), // <--- Add this line
        }
      });
      
      // Resolve the promise
      resolve(responseData);
    } catch (error) {
      console.error("Error processing response:", error);
      reject(error);
    } finally {
      // Clean up
      this.pendingRequests.delete(incomingId);
      clearTimeout(this.pendingRequests.get(incomingId)?.timeout);
    }
  }

  // Submit a new scraping request
  async submitRequest(paramsForRequest) {
    await this.startConnection(); // Ensure connection exists

    const userInfo = sessionService.getUser();
    const loginLegacyUserID = userInfo?.legacyUserID || -123456;
    const loginStateID = userInfo?.stateID || -123456;
    const blockID = paramsForRequest.blockID; // Hardcoded as in original

    const requestApiUrl = await ApiEndpoints.SignalRScrapeRequestApiUrl();

    try {
      const response = await fetch(`${this.apiMainFrameBaseUrl}${requestApiUrl.ApiUrl}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'MainframeInteractionApiKey': ApiKeyManager.GetMainframeScrapeApiKey(), // <--- Add this line
        },
        body: JSON.stringify({
          legacyId: loginLegacyUserID.toString(),
          stateId: loginStateID.toString(),
          blockId: blockID.toString(),
          requestJson: JSON.stringify(paramsForRequest.requestJSON),
        }),
      });

      const { requestId, message, type } = await response.json();
      
      if (!response.ok) {
        const error = new ErrorModel(
          message || 'Signal R request failed',
          'SignalRScrapeService.SubmitRequest',
         { code: response.status, type }
        );
        throw error;
      }

      console.log(`Request submitted. RequestId: ${requestId}`);

      return new Promise((resolve, reject) => {
        // Set timeout for this request
        const timeout = setTimeout(() => {
          this.handleRequestTimeout(requestId, reject);
        }, this.timeoutDuration);

        // Store the resolver and timeout
        this.pendingRequests.set(requestId, { resolve, reject, timeout });
      });
    } catch (error) {
      console.error("Error submitting request:", error);
      throw error;
    }
  }

  // Handle request timeout
  async handleRequestTimeout(requestId, reject) {
    console.warn(`Timeout: No response for RequestId ${requestId}`);
    
    const timeoutApiUrl = await ApiEndpoints.SignalRScrapeTimeoutApiUrl();

    try {
      await fetch(`${this.apiMainFrameBaseUrl}${timeoutApiUrl.ApiUrl}${requestId}`, { 
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'MainframeInteractionApiKey': ApiKeyManager.GetMainframeScrapeApiKey(), // <--- Add this line
        }
      });
      reject(new Error("Request timed out"));
    } catch (error) {
      console.error("Error marking request as timed out:", error);
      reject(new Error("Request timed out (backend update failed)"));
    } finally {
      this.pendingRequests.delete(requestId);
    }
  }

  // Clean up connection
  async stopConnection() {
    if (this.connection) {
      // Clear all pending requests
      this.pendingRequests.forEach(({ reject, timeout }) => {
        clearTimeout(timeout);
        reject(new Error("Connection stopped"));
      });
      this.pendingRequests.clear();
      
      await this.connection.stop();
      this.connection = null;
      console.log("SignalR connection stopped");
    }
  }
}

// Export a singleton instance
//export const scrapeService = new SignalRScrapeService();